import { type KeyboardEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, fmt, poNo, type CardPart, type User } from '../api.ts';
import Icon from '../components/Icon.tsx';
import ReleaseShortDialog from '../components/ReleaseShortDialog.tsx';
import CorrugationCardDialog from '../components/CorrugationCardDialog.tsx';
import SplitDialog from '../components/SplitDialog.tsx';
import Toast from '../components/Toast.tsx';
import UndoDialog from '../components/UndoDialog.tsx';

interface Work {
  kind: 'JOB' | 'LINE';
  job_id: number | null; job_number: string | null; po_item_id: number;
  ordered_quantity: string; production_quantity: string | null;
  box_name: string; box_size: string | null; customer_name: string;
  po_number: string | null; created_date: string | null;
  delivery_date: string | null; priority: string;
  delivery_state: string; board_required: boolean; board_ready: boolean;
  board_lines: string; board_lines_available: string;
  board_status: BoardStatus | null; board_short: string | null; board_sheets: string | null;
  board_paired: string | null; first_board: string | null; board_count: string | null;
  board_free: string | null; board_wanted: string | null;
  sheets_out_of_date: boolean | null;
  plate_missing: boolean | null;
  board_on_order: string | null; board_on_order_due: string | null;
  has_printing: boolean | null; has_corrugation: boolean | null;
  corrugation_required: boolean | null;
  printing_ready: boolean | null; corrugation_ready: boolean | null;
  card_made_at: string | null; card_updated_at?: string | null; has_card: boolean; ready_to_release: boolean;
  unreleased_quantity: string;
  staged_at: string | null; is_staged: boolean;
  priority_rank: number; urgency_rank: number;
  split_id: number | null; sheets_by_hand: boolean | null;
}

type BoardStatus = 'NOT_NEEDED' | 'NO_BOARD' | 'AVAILABLE' | 'SHARED' | 'SHORT' | 'PAIRED' | 'ISSUED';
type Tab = 'STAGED' | 'QUEUE';
type Tone = 'ok' | 'soon' | 'late' | 'none';
type MenuAt = { row: Work; right: number; top?: number; bottom?: number };
type MenuItem = 'OPEN_CARD' | 'SPLIT' | 'UNDO_SPLIT';
type Notice = { text: string; undo?: () => void; print?: () => void };
interface Filters { company: string; delivery: string; board: string; card: string }

const DAY = 86_400_000;
const PAGE_SIZE = 25;
const REFRESH_MS = 15_000;
const NO_FILTERS: Filters = { company: '', delivery: '', board: '', card: '' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const rowKey = (d: Work) => `${d.kind}:${d.kind === 'JOB' ? d.job_id : d.po_item_id}`;
const label = (d: Work) => d.job_number ?? d.box_name;

function shortDate(s: string | null | undefined): string {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return s;
  return y === new Date().getFullYear() ? `${d} ${MONTHS[m - 1]}` : `${d} ${MONTHS[m - 1]} ${y}`;
}

function daysUntil(s: string): number {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(y!, m! - 1, d!).getTime() - today.getTime()) / DAY);
}

function fmtWhen(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  const day = d.toDateString() === new Date().toDateString()
    ? 'Today' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${day} · ${time}`;
}

function stagedAge(s: string | null): string {
  if (!s) return '';
  const days = Math.floor((Date.now() - new Date(s).getTime()) / DAY);
  return days >= 1 ? `Staged ${days} day${days === 1 ? '' : 's'} ago` : '';
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  if (/failed to fetch|load failed|networkerror/i.test(err.message)) {
    return "Can't reach the server. Check the connection and try again.";
  }
  return err.message;
}

function cardMissing(d: Work): { label: string; part?: CardPart }[] {
  const out: { label: string; part?: CardPart }[] = [];
  if (d.board_status === 'NO_BOARD') out.push({ label: 'Board' });
  if (d.has_printing && !d.printing_ready) {
    out.push({ label: d.plate_missing ? 'Plate set no.' : 'Plate', part: 'PRINTING' });
  }
  if (!d.has_printing && d.corrugation_required && !d.corrugation_ready) {
    out.push({ label: 'Ply and liner', part: 'CORRUGATION' });
  }
  return out;
}

const corrugationPending = (d: Work) => Boolean(d.has_printing && d.corrugation_required && !d.corrugation_ready);

function blockers(d: Work): string[] {
  const out = cardMissing(d).map((m) => m.label === 'Board' ? 'Board not chosen' : `${m.label} missing`);
  if (d.board_status === 'SHORT') out.push('Board short');
  if (Number(d.unreleased_quantity) < 0) out.push('Order cut below released');
  else if (Number(d.ordered_quantity) > Number(d.unreleased_quantity)) {
    out.push(`Only ${fmt(d.unreleased_quantity)} left on the order`);
  }
  return out;
}

const isReady = (d: Work) => d.has_card && blockers(d).length === 0;

function cardIssues(d: Work): string[] {
  if (!d.has_card) return [];
  const out: string[] = [];
  if (d.has_printing && !d.printing_ready) out.push(d.plate_missing ? 'Plate set no. missing' : 'Plate missing');
  if (!d.has_printing && d.corrugation_required && !d.corrugation_ready) out.push('Ply and liner missing');
  if (corrugationPending(d)) out.push('Corrugation pending');
  if (d.sheets_out_of_date) out.push('Sheets out of date');
  return out;
}

const cardState = (d: Work): 'NONE' | 'PENDING' | 'DONE' =>
  !d.has_card ? 'NONE' : cardIssues(d).length ? 'PENDING' : 'DONE';

const BOARD_OK: (BoardStatus | null)[] = ['AVAILABLE', 'PAIRED', 'SHARED', 'NOT_NEEDED', 'ISSUED'];

interface FactValue { tone: Tone; text: string; sub?: string; title?: string }

function boardFact(d: Work): FactValue | null {
  if (d.kind === 'LINE' || d.board_status === null) return null;
  const n = (v: string | null) => fmt(v ?? '0');
  const held = Number(d.board_paired ?? 0) > 0 ? `${n(d.board_paired)} held` : undefined;
  switch (d.board_status) {
    case 'NOT_NEEDED': return { tone: 'none', text: 'Not needed' };
    case 'NO_BOARD': return { tone: 'none', text: 'Not chosen' };
    case 'AVAILABLE': return { tone: 'ok', text: 'Available', sub: held };
    case 'PAIRED': return { tone: 'ok', text: 'Paired', title: 'Stocked in for this job' };
    case 'SHARED':
      return {
        tone: 'soon', text: 'Shared', sub: held,
        title: `${n(d.board_free)} free · waiting jobs want ${n(d.board_wanted)}. Whichever goes to production first takes it.`,
      };
    case 'SHORT':
      return {
        tone: 'soon', text: `Short ${n(d.board_short)}`,
        sub: Number(d.board_on_order ?? 0) > 0
          ? (d.board_on_order_due ? `Due ${shortDate(d.board_on_order_due)}` : 'On order')
          : 'Not ordered',
      };
    default: return { tone: 'none', text: 'Taken' };
  }
}

function cardFact(d: Work): FactValue {
  if (!d.has_card) return { tone: 'none', text: 'Not written' };
  const issues = cardIssues(d);
  if (!issues.length) return { tone: 'ok', text: 'Complete' };
  return {
    tone: 'soon', text: issues[0]!,
    sub: issues.length > 1 ? `and ${issues.length - 1} more` : undefined,
    title: issues.join(' · '),
  };
}

function quantityNote(d: Work): { text: string; warn: boolean } | null {
  const left = Number(d.unreleased_quantity);
  if (left < 0) return { text: 'Order cut', warn: true };
  if (Number(d.ordered_quantity) > left) return { text: `Only ${fmt(d.unreleased_quantity)} left`, warn: true };
  if (d.production_quantity != null && Number(d.production_quantity) !== Number(d.ordered_quantity)) {
    return { text: `Run ${fmt(d.production_quantity)}`, warn: false };
  }
  return null;
}

function deliveryNote(d: Work): { text: string; tone: Tone } | null {
  if (!d.delivery_date) return null;
  const n = daysUntil(d.delivery_date);
  if (d.delivery_state === 'LATE' || n < 0) {
    return { text: n < 0 ? `${-n} day${n === -1 ? '' : 's'} late` : 'Due today', tone: 'late' };
  }
  if (d.delivery_state === 'DUE_SOON') {
    return { text: n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : `In ${n} days`, tone: 'soon' };
  }
  return null;
}

function Fact({ v }: { v: FactValue | null }) {
  if (!v) return <span className="dim">—</span>;
  return (
    <div className="pp-fact" title={v.title}>
      <div className="pp-fact-main"><span className={`pp-dot ${v.tone}`} /><span className="pp-ellipsis">{v.text}</span></div>
      {v.sub && <div className="pp-sub">{v.sub}</div>}
    </div>
  );
}

function matches(d: Work, f: Filters): boolean {
  if (f.company && d.customer_name !== f.company) return false;
  if (f.delivery === 'LATE' && d.delivery_state !== 'LATE') return false;
  if (f.delivery === 'SOON' && d.delivery_state !== 'DUE_SOON') return false;
  if (f.delivery === 'NONE' && d.delivery_date) return false;
  if (f.board === 'READY' && !BOARD_OK.includes(d.board_status)) return false;
  if (f.board === 'SHORT' && d.board_status !== 'SHORT') return false;
  if (f.board === 'NOT_CHOSEN' && d.board_status !== 'NO_BOARD') return false;
  if (f.card && cardState(d) !== f.card) return false;
  return true;
}

const COUNTS: { key: string; label: string; tone: Tone; field: keyof Filters; value: string; test: (d: Work) => boolean }[] = [
  { key: 'late', label: 'Late', tone: 'late', field: 'delivery', value: 'LATE', test: (d) => d.delivery_state === 'LATE' },
  { key: 'soon', label: 'Due soon', tone: 'soon', field: 'delivery', value: 'SOON', test: (d) => d.delivery_state === 'DUE_SOON' },
  { key: 'short', label: 'Board short', tone: 'soon', field: 'board', value: 'SHORT', test: (d) => d.board_status === 'SHORT' },
  { key: 'nocard', label: 'No card', tone: 'none', field: 'card', value: 'NONE', test: (d) => !d.has_card },
];

const BOARD_RANK: Record<BoardStatus, number> = {
  NO_BOARD: 0, SHORT: 1, SHARED: 2, AVAILABLE: 3, PAIRED: 4, NOT_NEEDED: 5, ISSUED: 6,
};
const CARD_RANK = { NONE: 0, PENDING: 1, DONE: 2 };

type SortValue = (r: Work) => string | number | null;
interface Col { key: string; label: string; width?: number; num?: boolean; cls?: string; value?: SortValue }

const COLS: Col[] = [
  { key: 'stage', label: '', width: 40 },
  { key: 'job', label: 'Job', width: 106, value: (r) => r.job_number },
  { key: 'company', label: 'Company', value: (r) => r.customer_name },
  { key: 'box', label: 'Box', value: (r) => r.box_name },
  { key: 'qty', label: 'Quantity', width: 92, num: true, value: (r) => Number(r.ordered_quantity) },
  { key: 'delivery', label: 'Delivery', width: 96, cls: 'pp-gap', value: (r) => r.delivery_date },
  { key: 'board', label: 'Board', width: 112, value: (r) => (r.board_status ? BOARD_RANK[r.board_status] : null) },
  { key: 'card', label: 'Card', width: 136, value: (r) => CARD_RANK[cardState(r)] },
  { key: 'action', label: '', width: 212 },
];

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

function sortRows(rows: Work[], key: string, dir: 'asc' | 'desc'): Work[] {
  const value = COLS.find((c) => c.key === key)?.value;
  if (!value) return rows;
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    return collator.compare(String(va), String(vb)) * sign;
  });
}

function menuItems(d: Work, canStage: boolean): MenuItem[] {
  const out: MenuItem[] = [];
  const many = Number(d.ordered_quantity) > 1;
  if (d.kind === 'JOB' && d.job_id && d.has_card) out.push('OPEN_CARD');
  if (canStage && many && d.kind === 'JOB' && d.job_id) out.push('SPLIT');
  if (canStage && d.split_id) out.push('UNDO_SPLIT');
  return out;
}

const MENU_LABEL: Record<MenuItem, string> = {
  OPEN_CARD: 'Open job card',
  SPLIT: 'Split…',
  UNDO_SPLIT: 'Undo split',
};

interface ActionProps {
  d: Work; canEdit: boolean; canReleaseShort: boolean;
  confirming: boolean; releasing: boolean;
  onWrite: () => void; onEdit: (part?: CardPart) => void; onCorrugation: () => void;
  onAskRelease: () => void; onRelease: () => void; onCancelRelease: () => void;
  onReleaseShort: () => void;
}

function Action(p: ActionProps) {
  const { d } = p;
  if (!d.has_card) {
    return p.canEdit
      ? <button className="btn" onClick={p.onWrite}>Write job card</button>
      : <span className="dim">No card yet</span>;
  }
  const missing = cardMissing(d);
  if (!d.is_staged) {
    if (!p.canEdit) return null;
    const part = missing.find((m) => m.part)?.part ?? (corrugationPending(d) ? 'CORRUGATION' : undefined);
    return <button className="btn" onClick={() => p.onEdit(part)}>Edit card</button>;
  }
  if (missing.length) {
    if (!p.canEdit) return <span className="dim">Card not complete</span>;
    const only = missing.length === 1 ? missing[0]! : null;
    const text = !only ? 'Complete card'
      : only.label === 'Board' ? 'Choose board'
      : only.part === 'CORRUGATION' ? 'Complete corrugation' : 'Complete printing';
    return (
      <button className="btn"
              onClick={() => (only?.part === 'CORRUGATION' ? p.onCorrugation() : p.onEdit(missing.find((m) => m.part)?.part))}>
        {text}
      </button>
    );
  }
  if (d.board_status === 'SHORT') {
    return p.canReleaseShort
      ? <button className="btn outline" onClick={p.onReleaseShort}>Send without board</button>
      : <span className="dim">Waiting for board</span>;
  }
  if (blockers(d).length) return <span className="dim">Check the order</span>;
  if (!p.canEdit) return <span className="dim">Ready</span>;
  if (p.releasing) return <button className="btn primary" disabled>Sending…</button>;
  if (p.confirming) {
    return (
      <>
        <button className="btn primary" autoFocus onClick={p.onRelease}>
          {d.sheets_out_of_date ? 'Send anyway' : 'Yes, send'}
        </button>
        <button className="linkish" onClick={p.onCancelRelease}>Cancel</button>
      </>
    );
  }
  return <button className="btn primary" onClick={p.onAskRelease}>Move to production</button>;
}

function Details({ d, canEdit, onClose, onOpenCard, onEdit, onCorrugation, onGoBoard }: {
  d: Work; canEdit: boolean; onClose: () => void; onOpenCard: () => void;
  onEdit: (part?: CardPart) => void; onCorrugation: () => void; onGoBoard: () => void;
}) {
  const q = quantityNote(d);
  const board = boardFact(d);
  const issues = cardIssues(d);
  const kv = (k: string, v: ReactNode) => (
    <div className="pp-kv" key={k}><span>{k}</span><span>{v}</span></div>
  );
  const printing = !d.has_card ? 'Not written'
    : !d.printing_ready ? (d.plate_missing ? 'Plate set no. missing' : 'Plate missing') : 'Complete';
  const corrugation = !d.has_card ? 'Not written' : d.corrugation_ready ? 'Complete' : 'Pending';
  return (
    <>
      <div className="pp-drawer-scrim" onClick={onClose} />
      <aside className="pp-drawer" role="dialog" aria-label={`${label(d)} details`}>
        <div className="pp-drawer-head">
          <div style={{ minWidth: 0 }}>
            <h3 className="mono">{d.job_number ?? 'No job yet'}<MakeTag number={d.job_number} /></h3>
            <p>{d.customer_name} · {d.box_name}</p>
          </div>
          <button className="toast-x pp-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="pp-drawer-body">
          <h4>Order</h4>
          {kv('Customer PO', poNo(d.po_number) === 'NA' ? 'No PO' : d.po_number)}
          {kv('Box size', d.box_size ?? '—')}
          {kv('Quantity', fmt(d.ordered_quantity))}
          {d.production_quantity != null && Number(d.production_quantity) !== Number(d.ordered_quantity)
            && kv('Run', fmt(d.production_quantity))}
          {q?.warn && kv('On the order', <span className="pp-warn">{q.text}</span>)}
          {kv('Created', shortDate(d.created_date))}
          {kv('Delivery', d.delivery_date
            ? <>{shortDate(d.delivery_date)}{deliveryNote(d) && <span className={`pp-note ${deliveryNote(d)!.tone}`}> · {deliveryNote(d)!.text}</span>}</>
            : 'No date')}
          {d.is_staged && kv('Staged', fmtWhen(d.staged_at))}

          {board && (
            <>
              <h4>Board</h4>
              {kv('Status', <Fact v={{ ...board, sub: undefined }} />)}
              {Number(d.board_sheets ?? 0) > 0 && kv('Sheets', fmt(d.board_sheets))}
              {Number(d.board_paired ?? 0) > 0 && d.board_status !== 'PAIRED'
                && kv('Held', `${fmt(d.board_paired)} for this job`)}
              {d.board_status === 'SHARED' && kv('Free', `${fmt(d.board_free ?? '0')} · wanted ${fmt(d.board_wanted ?? '0')}`)}
              {Number(d.board_on_order ?? 0) > 0
                && kv('On order', `${fmt(d.board_on_order)}${d.board_on_order_due ? ` · due ${shortDate(d.board_on_order_due)}` : ''}`)}
              {d.board_status === 'SHORT' && Number(d.board_on_order ?? 0) === 0
                && kv('On order', <button className="linkish" onClick={onGoBoard}>Not ordered · Order board</button>)}
            </>
          )}

          <h4>Card</h4>
          {!d.has_card && kv('Status', <Fact v={cardFact(d)} />)}
          {d.has_card && d.has_printing && kv('Printing', printing)}
          {d.has_card && d.corrugation_required && kv('Corrugation', corrugation)}
          {d.sheets_out_of_date && kv('Sheets', <span className="pp-warn">Out of date</span>)}
          {d.has_card && kv('Updated', fmtWhen(d.card_updated_at ?? d.card_made_at))}
        </div>
        <div className="pp-drawer-foot">
          {canEdit && corrugationPending(d) && (
            <button className="btn" onClick={onCorrugation}>Complete corrugation</button>
          )}
          {canEdit && d.has_card && issues.some((i) => i !== 'Corrugation pending') && (
            <button className="btn" onClick={() => onEdit(cardMissing(d).find((m) => m.part)?.part)}>Edit card</button>
          )}
          <span className="spacer" />
          <button className="btn primary" onClick={onOpenCard}>{d.has_card ? 'Open job card' : 'Write job card'}</button>
        </div>
      </aside>
    </>
  );
}

export default function PrePress({
  user, onOpenCard, onOpenJob, onGoBoard,
}: {
  user: User;
  onOpenCard: (poItemId: number) => void;
  onOpenJob: (jobId: number, part?: CardPart, edit?: boolean) => void;
  onGoBoard: () => void;
}) {
  const [queue, setQueue] = useState<Work[]>([]);
  const [staged, setStaged] = useState<Work[]>([]);
  const [tab, setTab] = useState<Tab>('STAGED');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [staging, setStaging] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);
  const noticeKey = useMemo(() => Date.now(), [notice]);
  const [stale, setStale] = useState(false);
  const [menuFor, setMenuFor] = useState<MenuAt | null>(null);
  const [splitFor, setSplitFor] = useState<Work | null>(null);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);
  const [shortFor, setShortFor] = useState<Work | null>(null);
  const [corrFor, setCorrFor] = useState<Work | null>(null);
  const [batchFor, setBatchFor] = useState<Work[] | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const tabPicked = useRef(false);

  const canRelease = user.role === 'ADMIN' || user.role === 'MANAGER';
  const canReleaseShort = user.permissions.includes('prepress.release_short');
  const canStage = user.permissions.includes('prepress.stage');

  const load = useCallback(async (background = false) => {
    if (!background) setError('');
    try {
      const qs = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
      const [a, b] = await Promise.all([
        api.get<{ items: Work[] }>(`/api/pre-press/queue${qs}`),
        api.get<{ items: Work[] }>(`/api/pre-press/staged${qs}`),
      ]);
      setQueue(a.items);
      setStaged(b.items);
      setStale(false);
      if (!tabPicked.current) {
        tabPicked.current = true;
        if (b.items.length === 0 && a.items.length > 0) setTab('QUEUE');
      }
      if (background) setError('');
    } catch (err) {
      if (background) setStale(true);
      else setError(friendlyError(err, 'Could not load pre-press'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const loadRef = useRef(load);
  loadRef.current = load;
  const busyRef = useRef(false);
  busyRef.current = releasingId !== null || staging !== null || batchBusy;

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible' && !busyRef.current) void loadRef.current(true);
    };
    const timer = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  useEffect(() => {
    if (confirmId === null) return;
    const t = setTimeout(() => setConfirmId(null), 6000);
    return () => clearTimeout(t);
  }, [confirmId]);

  useEffect(() => {
    if (!openKey && !menuFor) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (menuFor) setMenuFor(null);
      else setOpenKey(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey, menuFor]);

  const tabRows = tab === 'STAGED' ? staged : queue;
  const companies = useMemo(
    () => Array.from(new Set([...queue, ...staged].map((r) => r.customer_name))).sort(collator.compare),
    [queue, staged],
  );
  const filtered = useMemo(() => tabRows.filter((r) => matches(r, filters)), [tabRows, filters]);
  const sorted = useMemo(() => (
    sortKey ? sortRows(filtered, sortKey, sortDir)
      : tab === 'STAGED' ? [...filtered].sort((a, b) => Number(isReady(b)) - Number(isReady(a)))
      : filtered
  ), [filtered, sortKey, sortDir, tab]);

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const from = sorted.length === 0 ? 0 : (current - 1) * PAGE_SIZE + 1;
  const to = Math.min(sorted.length, current * PAGE_SIZE);

  const anyFilter = search.trim() !== '' || Object.values(filters).some(Boolean);
  const batch = staged.filter((r) => isReady(r) && !r.sheets_out_of_date);
  const openRow = openKey ? [...staged, ...queue].find((r) => rowKey(r) === openKey) ?? null : null;

  useEffect(() => { setPage(1); }, [tab, search, filters, sortKey, sortDir]);
  useEffect(() => {
    if (openKey && !loading && !openRow) setOpenKey(null);
  }, [openKey, openRow, loading]);

  function setFilter(field: keyof Filters, value: string) {
    setFilters((f) => ({ ...f, [field]: value }));
  }

  function clearFilters() {
    setFilters(NO_FILTERS);
    setSearch('');
  }

  function sortOn(key: string) {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortKey(null); setSortDir('asc'); }
  }

  function switchTab(next: Tab) {
    setTab(next);
    setSortKey(null);
    setSortDir('asc');
    setConfirmId(null);
  }

  function writeCard(d: Work) {
    setNotice(null);
    if (d.kind === 'JOB') onOpenJob(d.job_id!);
    else onOpenCard(d.po_item_id);
  }

  async function toggleStage(d: Work, next: boolean, isUndo = false) {
    const kind = d.kind === 'JOB' ? 'jobs' : 'po-items';
    const id = d.kind === 'JOB' ? d.job_id! : d.po_item_id;
    setStaging(`${kind}:${id}`);
    setError('');
    try {
      await api.put(`/api/pre-press/${kind}/${id}/staged`, { staged: next });
      setNotice(isUndo ? null : {
        text: next ? `${label(d)} staged` : `${label(d)} back in the Queue`,
        undo: () => { setNotice(null); void toggleStage(d, !next, true); },
      });
      await load();
    } catch (err) {
      setError(friendlyError(err, 'Could not update staging'));
    } finally {
      setStaging(null);
    }
  }

  async function release(d: Work) {
    if (!d.job_id) return;
    setReleasingId(d.job_id);
    setError('');
    try {
      const res = await api.post<{ remainingInPrePress: number }>(`/api/pre-press/jobs/${d.job_id}/release`);
      // Released: the floor copy is what is printed next (086), so the card is one click away.
      const jobId = d.job_id;
      setNotice({
        text: `${d.job_number} sent to production`
          + (res.remainingInPrePress > 0 ? ` · ${fmt(res.remainingInPrePress)} still in Pre-Press on ${poNo(d.po_number)}` : ''),
        print: () => { setNotice(null); onOpenJob(jobId); },
      });
      await load();
    } catch (err) {
      setError(friendlyError(err, 'Could not move that job to production'));
    } finally {
      setReleasingId(null);
    }
  }

  async function sendBatch(rows: Work[]) {
    setBatchBusy(true); setError('');
    const sent: string[] = [];
    const refused: string[] = [];
    for (const d of rows) {
      try {
        await api.post(`/api/pre-press/jobs/${d.job_id}/release`);
        sent.push(d.job_number ?? '');
      } catch (err) {
        refused.push(`${d.job_number}: ${friendlyError(err, 'refused')}`);
      }
    }
    setBatchBusy(false); setBatchFor(null);
    if (sent.length) setNotice({ text: `${sent.length} sent to production · ${sent.join(', ')}` });
    if (refused.length) setError(`Not sent: ${refused.join(' · ')}`);
    await load();
  }

  function openMenu(e: MouseEvent<HTMLButtonElement>, d: Work) {
    const r = e.currentTarget.getBoundingClientRect();
    const right = window.innerWidth - r.right;
    setMenuFor(window.innerHeight - r.bottom < 200
      ? { row: d, right, bottom: window.innerHeight - r.top + 6 }
      : { row: d, right, top: r.bottom + 6 });
  }

  function onRowClick(e: MouseEvent<HTMLTableRowElement>, d: Work) {
    if ((e.target as HTMLElement).closest('button, a, input, select, label')) return;
    const k = rowKey(d);
    setOpenKey(openKey === k ? null : k);
  }

  function onRowKey(e: KeyboardEvent<HTMLTableRowElement>, d: Work) {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    setOpenKey(rowKey(d));
  }

  const emptyText = anyFilter
    ? 'No jobs match the search or filters.'
    : tab === 'STAGED'
      ? 'Nothing staged. Star jobs in the Queue to plan the day.'
      : 'Nothing in the Queue.';

  return (
    <>
      {error && <div className="error" style={{ margin: '0 0 16px' }}>{error}</div>}
      {notice && (
        <Toast key={noticeKey} onClose={() => setNotice(null)}>
          <span>{notice.text}</span>
          {notice.print && <button className="toast-act" onClick={notice.print}>Print card</button>}
          {notice.undo && <button className="toast-act" onClick={notice.undo}>Undo</button>}
        </Toast>
      )}

      <div className="panel">
        <div className="pp-head">
          <div className="detail-tabs" role="tablist">
            {([['STAGED', 'Staged', staged.length], ['QUEUE', 'Queue', queue.length]] as [Tab, string, number][])
              .map(([key, text, n]) => (
                <button key={key} role="tab" aria-selected={tab === key} onClick={() => switchTab(key)}>
                  {text}<span className="n">{loading ? '…' : fmt(n)}</span>
                </button>
              ))}
          </div>
          <span className="spacer" />
          {stale && <span className="dim pp-stale">Couldn't refresh · retrying</span>}
          {COUNTS.map((c) => {
            const n = tabRows.filter(c.test).length;
            if (n === 0) return null;
            const on = filters[c.field] === c.value;
            return (
              <button key={c.key} className={`pp-count${on ? ' on' : ''}`} aria-pressed={on}
                      onClick={() => setFilter(c.field, on ? '' : c.value)}>
                <span className={`pp-dot ${c.tone}`} />{c.label}<strong>{fmt(n)}</strong>
              </button>
            );
          })}
          {tab === 'STAGED' && canRelease && batch.length >= 2 && (
            <button className="btn primary" onClick={() => { setNotice(null); setBatchFor(batch); }}>
              Send {batch.length} ready
            </button>
          )}
        </div>

        <div className="pp-toolbar">
          <input className="search" placeholder="Search job, company, box or PO" aria-label="Search"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="pp-select" aria-label="Company" value={filters.company}
                  onChange={(e) => setFilter('company', e.target.value)}>
            <option value="">All companies</option>
            {companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="pp-select" aria-label="Delivery" value={filters.delivery}
                  onChange={(e) => setFilter('delivery', e.target.value)}>
            <option value="">Any delivery</option>
            <option value="LATE">Late</option>
            <option value="SOON">Due soon</option>
            <option value="NONE">No date</option>
          </select>
          <select className="pp-select" aria-label="Board" value={filters.board}
                  onChange={(e) => setFilter('board', e.target.value)}>
            <option value="">Any board</option>
            <option value="READY">Board ready</option>
            <option value="SHORT">Board short</option>
            <option value="NOT_CHOSEN">Not chosen</option>
          </select>
          <select className="pp-select" aria-label="Card" value={filters.card}
                  onChange={(e) => setFilter('card', e.target.value)}>
            <option value="">Any card</option>
            <option value="NONE">Not written</option>
            <option value="PENDING">Pending</option>
            <option value="DONE">Complete</option>
          </select>
          {anyFilter && <button className="btn" onClick={clearFilters}>Clear</button>}
        </div>

        {!loading && sorted.length === 0 ? (
          <div className="empty">
            {emptyText}
            {anyFilter && <> <button className="linkish" onClick={clearFilters}>Clear filters</button></>}
          </div>
        ) : (
          <>
            <div className="tbl">
              <table className="pp-table">
                <colgroup>
                  {COLS.map((c) => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
                </colgroup>
                <thead>
                  <tr>
                    {COLS.map((c) => {
                      const cls = [c.num ? 'num' : '', c.cls ?? '', c.value ? 'sortable' : ''].filter(Boolean).join(' ');
                      if (!c.value) {
                        return <th key={c.key} className={cls}>{c.key === 'stage' && <span className="sr-only">Staged</span>}</th>;
                      }
                      const on = sortKey === c.key;
                      return (
                        <th key={c.key} className={cls} tabIndex={0}
                            aria-sort={on ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                            onClick={() => sortOn(c.key)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sortOn(c.key); } }}>
                          {c.label}
                          {on && <span className="sort-mark">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((d) => {
                    const k = rowKey(d);
                    const busy = staging === `${d.kind === 'JOB' ? 'jobs' : 'po-items'}:${d.kind === 'JOB' ? d.job_id : d.po_item_id}`;
                    const urgent = d.priority === 'URGENT' || d.priority === 'HIGH';
                    const age = d.is_staged ? stagedAge(d.staged_at) : '';
                    const q = quantityNote(d);
                    const due = deliveryNote(d);
                    const items = menuItems(d, canStage);
                    return (
                      <tr key={k} className={openKey === k ? 'on' : undefined} tabIndex={0}
                          aria-label={`${label(d)} · ${d.customer_name}`}
                          onClick={(e) => onRowClick(e, d)} onKeyDown={(e) => onRowKey(e, d)}>
                        <td className="pp-star">
                          <button className={`icon-btn pick${d.is_staged ? ' on' : ''}`}
                                  disabled={busy}
                                  title={d.is_staged ? 'Send back to the Queue' : 'Stage'}
                                  aria-label={d.is_staged ? `Send ${label(d)} back to the Queue` : `Stage ${label(d)}`}
                                  aria-pressed={d.is_staged}
                                  onClick={() => void toggleStage(d, !d.is_staged)}>
                            <Icon name="star" size={14} />
                          </button>
                        </td>
                        <td>
                          {d.job_number ? (
                            <button className="linkish mono pp-job" title="Open the job card"
                                    onClick={() => { setNotice(null); onOpenJob(d.job_id!); }}>
                              {d.job_number}
                            </button>
                          ) : <span className="dim">No job yet</span>}
                          <MakeTag number={d.job_number} />
                          {(urgent || age) && (
                            <div className="pp-sub">
                              {urgent && (
                                <span className={d.priority === 'URGENT' ? 'pp-late' : 'pp-high'}>
                                  {d.priority === 'URGENT' ? 'Urgent' : 'High'}
                                </span>
                              )}
                              {urgent && age && ' · '}
                              {age}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="pp-wrap" title={d.customer_name}>{d.customer_name}</div>
                          {poNo(d.po_number) !== 'NA' && (
                            <div className="pp-sub pp-ellipsis">{/^po\b/i.test(d.po_number!) ? d.po_number : `PO ${d.po_number}`}</div>
                          )}
                        </td>
                        <td>
                          <div className="pp-wrap" title={d.box_name}>{d.box_name}</div>
                          {d.box_size && <div className="pp-sub pp-ellipsis">{d.box_size}</div>}
                        </td>
                        <td className="num">
                          <div className="pp-qty">{fmt(d.ordered_quantity)}</div>
                          {q && <div className={`pp-sub${q.warn ? ' pp-warn' : ''}`}>{q.text}</div>}
                        </td>
                        <td className="pp-gap">
                          <div>{shortDate(d.delivery_date)}</div>
                          {due && <div className={`pp-sub pp-${due.tone}`}>{due.text}</div>}
                        </td>
                        <td><Fact v={boardFact(d)} /></td>
                        <td><Fact v={cardFact(d)} /></td>
                        <td>
                          <div className="pp-act">
                            <div className="pp-act-main">
                              <Action d={d} canEdit={canRelease} canReleaseShort={canReleaseShort}
                                      confirming={confirmId === d.job_id} releasing={releasingId === d.job_id}
                                      onWrite={() => writeCard(d)}
                                      onEdit={(part) => { setNotice(null); onOpenJob(d.job_id!, part, true); }}
                                      onCorrugation={() => { setNotice(null); setCorrFor(d); }}
                                      onAskRelease={() => setConfirmId(d.job_id)}
                                      onRelease={() => { setConfirmId(null); void release(d); }}
                                      onCancelRelease={() => setConfirmId(null)}
                                      onReleaseShort={() => { setNotice(null); setShortFor(d); }} />
                            </div>
                            {items.length > 0
                              ? (
                                <button className="btn pp-more" title="More" aria-label={`More for ${label(d)}`}
                                        aria-haspopup="menu" onClick={(e) => openMenu(e, d)}>
                                  ⋯
                                </button>
                              )
                              : <span className="pp-more-gap" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <span className="dim">
                {loading ? 'Loading…' : `${fmt(from)}–${fmt(to)} of ${fmt(sorted.length)}`}
                {!loading && anyFilter && ` · ${fmt(tabRows.length)} in ${tab === 'STAGED' ? 'Staged' : 'the Queue'}`}
              </span>
              <span className="spacer" />
              {pages > 1 && (
                <>
                  <button className="btn" disabled={current <= 1} onClick={() => setPage(current - 1)}>Prev</button>
                  <span className="dim">{current} / {pages}</span>
                  <button className="btn" disabled={current >= pages} onClick={() => setPage(current + 1)}>Next</button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {menuFor && (
        <>
          <div className="menu-scrim" onClick={() => setMenuFor(null)} />
          <div className="menu" role="menu"
               style={{ position: 'fixed', top: menuFor.top, bottom: menuFor.bottom, right: menuFor.right, left: 'auto' }}>
            {menuItems(menuFor.row, canStage).map((m, i, all) => (
              <span key={m}>
                {i > 0 && (m === 'UNDO_SPLIT' || (all[i - 1] === 'OPEN_CARD')) && <div className="menu-sep" />}
                <button role="menuitem" onClick={() => {
                  const row = menuFor.row;
                  setMenuFor(null); setNotice(null);
                  if (m === 'OPEN_CARD') onOpenJob(row.job_id!);
                  else if (m === 'UNDO_SPLIT') setUndoUrl(`/api/pre-press/splits/${row.split_id}/undo`);
                  else setSplitFor(row);
                }}>
                  {MENU_LABEL[m]}
                </button>
              </span>
            ))}
          </div>
        </>
      )}

      {openRow && (
        <Details d={openRow} canEdit={canRelease}
                 onClose={() => setOpenKey(null)}
                 onOpenCard={() => { setOpenKey(null); writeCard(openRow); }}
                 onEdit={(part) => { setOpenKey(null); onOpenJob(openRow.job_id!, part, true); }}
                 onCorrugation={() => { setOpenKey(null); setCorrFor(openRow); }}
                 onGoBoard={onGoBoard} />
      )}

      {splitFor && (
        <SplitDialog row={splitFor}
                     onClose={() => setSplitFor(null)}
                     onDone={(msg, splitId) => {
                       setNotice({
                         text: msg,
                         undo: () => { setNotice(null); setUndoUrl(`/api/pre-press/splits/${splitId}/undo`); },
                       });
                       void load();
                     }} />
      )}
      {undoUrl && (
        <UndoDialog previewUrl={undoUrl}
                    onClose={() => setUndoUrl(null)}
                    onDone={(msg) => { setNotice({ text: msg }); void load(); }} />
      )}
      {batchFor && (
        <div className="backdrop" onClick={() => !batchBusy && setBatchFor(null)}>
          <div className="modal" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Send {batchFor.length} to production</h3>
              <p>Each is staged with its card complete and its board ready.</p>
            </div>
            <div className="modal-body">
              <div className="order-list" style={{ marginBottom: 0 }}>
                {batchFor.map((d) => (
                  <div key={d.job_id} className="order-row">
                    <strong className="mono">{d.job_number}</strong>
                    <span style={{ flex: 1 }}>{d.customer_name} · {d.box_name}</span>
                    <span className="dim">{fmt(d.ordered_quantity)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" disabled={batchBusy} onClick={() => setBatchFor(null)}>Cancel</button>
              <button className="btn primary" disabled={batchBusy} autoFocus onClick={() => void sendBatch(batchFor)}>
                {batchBusy ? 'Sending…' : `Send ${batchFor.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {corrFor?.job_id && (
        <CorrugationCardDialog jobId={corrFor.job_id}
                               onClose={() => setCorrFor(null)}
                               onDone={(msg) => { setCorrFor(null); setNotice({ text: msg }); void load(); }} />
      )}
      {shortFor?.job_id && (
        <ReleaseShortDialog jobId={shortFor.job_id} jobNumber={shortFor.job_number ?? ''}
                            onClose={() => setShortFor(null)}
                            onDone={(msg) => { setShortFor(null); setNotice({ text: msg }); void load(); }} />
      )}
    </>
  );
}
